import React from "react";
import { api, formatPHP } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function AdminRates() {
  const [rates, setRates] = React.useState([]);

  const load = () => api.get("/rates").then((r) => setRates(r.data || []));
  React.useEffect(() => { load(); }, []);

  const update = (idx, field, value) => {
    const next = [...rates];
    next[idx] = {
      ...next[idx],
      [field]: field === "price" || field === "weekend_price" ? Number(value) : value,
    };
    setRates(next);
  };

  const save = async (rate) => {
    try {
      await api.put(`/rates/${rate.type}`, {
        label: rate.label,
        time_window: rate.time_window,
        price: rate.price,
        weekend_price: Number(rate.weekend_price) || 0,
        description: rate.description,
      });
      toast.success(`${rate.label} updated`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <div data-testid="admin-rates-page">
      <div className="mb-8">
        <p className="overline mb-2">Pricing</p>
        <h1 className="font-serif-display text-4xl">Booking Rates</h1>
        <p className="text-[#A1A4B5] mt-2 text-sm max-w-xl">
          These rates appear on the public booking form. Downpayment percentage is set in Settings.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {rates.map((r, idx) => (
          <div key={r.type} className="border border-[var(--border)] rounded-2xl p-6 bg-[#13151D]" data-testid={`rate-card-${r.type}`}>
            <div className="overline mb-3">{r.type.replace("_", " ")}</div>
            <h3 className="font-serif-display text-2xl">{r.label}</h3>
            <div className="text-[#C5A059] font-medium mt-1">
              {formatPHP(r.price)}
              {Number(r.weekend_price) > 0 && (
                <span className="text-[#A1A4B5] text-xs ml-2">
                  · weekends {formatPHP(r.weekend_price)}
                </span>
              )}
            </div>

            <div className="space-y-3 mt-5">
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Label</Label>
                <Input
                  value={r.label}
                  onChange={(e) => update(idx, "label", e.target.value)}
                  className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                  data-testid={`rate-label-${r.type}`}
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Time Window</Label>
                <Input
                  value={r.time_window}
                  onChange={(e) => update(idx, "time_window", e.target.value)}
                  className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                  data-testid={`rate-time-${r.type}`}
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Price · Mon–Thu (PHP)
                </Label>
                <Input
                  type="number"
                  value={r.price}
                  onChange={(e) => update(idx, "price", e.target.value)}
                  className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                  data-testid={`rate-price-${r.type}`}
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Weekend Price · Fri–Sun (PHP)
                  <span className="normal-case tracking-normal text-[#A1A4B5]/60 ml-1">(0 = same as weekday)</span>
                </Label>
                <Input
                  type="number"
                  value={r.weekend_price ?? 0}
                  onChange={(e) => update(idx, "weekend_price", e.target.value)}
                  className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                  data-testid={`rate-weekend-price-${r.type}`}
                  placeholder="e.g. 12000"
                />
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Description</Label>
                <Textarea
                  value={r.description}
                  onChange={(e) => update(idx, "description", e.target.value)}
                  className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                  rows={2}
                  data-testid={`rate-description-${r.type}`}
                />
              </div>
              <Button onClick={() => save(rates[idx])} className="gold-btn rounded-full w-full" data-testid={`rate-save-${r.type}`}>
                Save
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
